import { useTranslation } from "react-i18next";
import { GetExpeditionStatsResponseDto } from "@/types/api/api-types";

type CardsProps = {
  expeditionStats?: GetExpeditionStatsResponseDto["data"];
  previousExpeditionStats?: GetExpeditionStatsResponseDto["data"];
  timeScale: "year" | "month" | "week";
};

export const Cards = ({ expeditionStats, previousExpeditionStats, timeScale }: CardsProps) => {
  const { t } = useTranslation("boClients");
  const statsCount = expeditionStats?.length || 0;
  const previousStatsCount = previousExpeditionStats?.length || 0;
  const countChange = ((statsCount - previousStatsCount) / previousStatsCount) * 100;

  const pelicanFeesTotal = (data: GetExpeditionStatsResponseDto["data"][0]) => {
    return (
      data.expeditionBillingDetail.expeditionInvoiceData.insurance / 100 +
      data.expeditionBillingDetail.expeditionInvoiceData.packaging / 100 +
      data.expeditionBillingDetail.expeditionInvoiceData.transportHt / 100 +
      data.expeditionBillingDetail.expeditionInvoiceData.packagingProtection / 100 +
      data.expeditionBillingDetail.expeditionInvoiceData.taxes / 100 +
      data.expeditionBillingDetail.expeditionInvoiceData.tva / 100
    );
  };

  const calculateCustomCotationsSales = (data?: GetExpeditionStatsResponseDto["data"]) => {
    return (
      data?.reduce((acc, expedition) => {
        if (expedition.customQuotation?.type === "PALETTE") {
          expedition.customQuotation.paletteDetail?.palettePackages?.forEach((el) => {
            acc += el.value;
          });
        } else if (expedition.customQuotation?.type === "PARCEL") {
          expedition.customQuotation.parcelDetail?.parcelPackages?.forEach((el) => {
            acc += el.value;
          });
        } else if (expedition.customQuotation?.type === "OTHER") {
          acc += expedition.customQuotation.otherDetail?.value || 0;
        }
        return acc;
      }, 0) || 0
    );
  };

  const formatChange = (change: number) => {
    const value = `${change > 0 ? "+" : ""}${change.toFixed(0)}`;
    if (timeScale === "week")
      return t("profileStatisticsCards.changeVsPrevious.week", { change: value });
    if (timeScale === "month")
      return t("profileStatisticsCards.changeVsPrevious.month", { change: value });
    return t("profileStatisticsCards.changeVsPrevious.year", { change: value });
  };

  const cotationsSales =
    expeditionStats?.reduce(
      (acc, expedition) =>
        acc +
        (expedition.quotation && expedition.quotation.deliveryContent
          ? expedition.quotation.deliveryContent.reduce(
              (acc, content) => acc + content.unitPrice * content.numberOfBottles,
              0,
            )
          : 0),
      0,
    ) || 0;
  const previousCotationsSales =
    previousExpeditionStats?.reduce(
      (acc, expedition) =>
        acc +
        (expedition.quotation && expedition.quotation.deliveryContent
          ? expedition.quotation.deliveryContent.reduce(
              (acc, content) => acc + content.unitPrice * content.numberOfBottles,
              0,
            )
          : 0),
      0,
    ) || 0;
  const expeditionsPelicanFees =
    expeditionStats?.reduce((acc, expedition) => acc + pelicanFeesTotal(expedition) || 0, 0) || 0;
  const previousExpeditionsPelicanFees =
    previousExpeditionStats?.reduce(
      (acc, expedition) => acc + pelicanFeesTotal(expedition) || 0,
      0,
    ) || 0;
  const expeditionsOtherFees =
    expeditionStats?.reduce((acc, expedition) => acc + (expedition.quotation?.otherFees || 0), 0) ||
    0;
  const previousExpeditionsOtherFees =
    previousExpeditionStats?.reduce(
      (acc, expedition) => acc + (expedition.quotation?.otherFees || 0),
      0,
    ) || 0;
  const customCotationsSales = calculateCustomCotationsSales(expeditionStats);

  const previousCustomCotationsSales = calculateCustomCotationsSales(previousExpeditionStats);

  const expeditionsTurnover =
    cotationsSales + expeditionsPelicanFees + expeditionsOtherFees + customCotationsSales;

  const previousExpeditionsTurnover =
    previousCotationsSales +
    previousExpeditionsPelicanFees +
    previousExpeditionsOtherFees +
    previousCustomCotationsSales;

  const turnoverChange =
    ((expeditionsTurnover + previousExpeditionsTurnover) / previousExpeditionsTurnover) * 100;

  const totalTurnover = expeditionsTurnover.toFixed(0);

  return (
    <div className="flex w-full flex-col gap-4 md:flex-row md:gap-8">
      <div className="flex flex-1 flex-col gap-2 rounded-xl border bg-white p-6 shadow-xs">
        <span className="text-sm font-medium text-gray-600">
          {t("profileStatisticsCards.validatedExpeditions")}
        </span>
        <div className="flex w-full flex-col">
          <span className="text-2xl font-semibold text-gray-950">{statsCount}</span>
          <span className="text-sm font-normal text-gray-400">{formatChange(countChange)}</span>
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-2 rounded-xl border bg-white p-6 shadow-xs">
        <span className="text-sm font-medium text-gray-600">
          {t("profileStatisticsCards.turnover")}
        </span>
        <div className="flex w-full flex-col">
          <span className="text-2xl font-semibold text-gray-950">{totalTurnover} €</span>
          <span className="text-sm font-normal text-gray-400">{formatChange(turnoverChange)}</span>
        </div>
      </div>
    </div>
  );
};
